import axios from "axios";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { Edit2, Eye, MoreHorizontalIcon, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "src/components/ui/sheet";
import { Textarea } from "src/components/ui/textarea";
import { DataTable } from "../components/DataTable";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { BaseUrlApi, ErrorMessage } from "../lib/api";
import SkeletonTable from "./SkeletonTable";
import { DialogDelete } from "../components/Common/DialogDelete";

export default function Categories() {
  const columns = [
    {
      accessorKey: "title",
      header: "العنوان",
      cell: ({ row }) => row.original.title || "___",
    },
    // {
    //   accessorKey: "description",
    //   header: "الوصف",
    //   cell: ({ row }) => row.original.description || "___",
    // },
    // {
    //   accessorKey: "created_at",
    //   header: "تاريخ الإنشاء",
    //   cell: ({ row }) =>
    //     dayjs(row.original.created_at).format("DD/MM/YYYY hh:mm A"),
    // },
    // {
    //   accessorKey: "updated_at",
    //   header: "تاريخ التعديل",
    //   cell: ({ row }) =>
    //     dayjs(row.original.updated_at).format("DD/MM/YYYY hh:mm A"),
    // },
    {
      id: "Actions",
      header: "الإجراءات",
      cell: ({ row }) => (
        <>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="rounded-lg px-2 border-none text-yellow-600"
            >
              <Eye />
            </Button>
            <Button
              size="sm"
              className="rounded-lg px-2 text-green-600"
              variant="ghost"
              onClick={() => {
                setShowEditSheet(true);
                setEditRecord(row.original);
              }}
            >
              <Edit2 />
            </Button>
            <DialogDelete
              title={"القسم"}
              data={row.original.title}
              apiUrl={`/categories/${row.original.bid}`}
              fetchData={handleFetchCategories}
            />
          </div>
        </>
      ),
    },
  ];

  // Categories
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categories, setCategories] = useState([]);
  const handleFetchCategories = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/categories`);
      setCategories(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    setLoadingCategories(true);
    handleFetchCategories();
  }, []);

  const exportData = categories.map((row) => ({
    bid: row.bid,
    title: row.title || "___",
    description: row.description || "___",
    created_at: new Date(row.created_at).toLocaleString("ar-EG", {
      dateStyle: "medium",
      timeStyle: "short",
    }),
    updated_at: new Date(row.updated_at).toLocaleString("ar-EG", {
      dateStyle: "medium",
      timeStyle: "short",
    }),
  }));

  const exportColumns = [
    { accessorKey: "bid", header: "رقم التعريف" },
    { accessorKey: "title", header: "العنوان" },
    { accessorKey: "description", header: "الوصف" },
    { accessorKey: "created_at", header: "تاريخ الإنشاء" },
    { accessorKey: "updated_at", header: "تاريخ التعديل" },
  ];

  const [showEditSheet, setShowEditSheet] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [loadinEdit, setLoadinEdit] = useState(false);

  const handleEdit = async (e) => {
    e.preventDefault();
    setLoadinEdit(true);

    try {
      await axios.put(`${BaseUrlApi}/categories/${editRecord.bid}`, {
        title: editRecord.title,
        description: editRecord.description,
      });
      toast("تم التعديل", { description: "تم تحديث تفاصيل القسم بنجاح ✏️" });

      await handleFetchCategories();
      setShowEditSheet(false);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadinEdit(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: "spring", duration: 2 }}
      dir="rtl"
      className="space-y-5"
    >
      <div className="flex items-center gap-2 justify-between">
        <h1 className="text-2xl font-bold">الأقسام</h1>
        <ShowAddSheet handleFetchCategories={handleFetchCategories} />
      </div>

      {loadingCategories ? (
        <SkeletonTable />
      ) : (
        <DataTable
          columns={columns}
          data={categories}
          title={"الأقسام"}
          filterNames={["title", "description"]}
          exportData={exportData}
          exportColumns={exportColumns}
        />
      )}

      {showEditSheet && (
        <Sheet open={showEditSheet} onOpenChange={setShowEditSheet}>
          <SheetContent side="left" dir="rtl">
            <SheetHeader className="text-start">
              <SheetTitle>قم بتعديل العنوان والوصف</SheetTitle>
            </SheetHeader>
            <div className="mt-3">
              <label className="block font-medium">العنوان</label>
              <Input
                className="w-full p-2 border rounded mt-1"
                placeholder="أدخل العنوان"
                value={editRecord.title}
                onChange={(e) =>
                  setEditRecord({ ...editRecord, title: e.target.value })
                }
              />
            </div>
            <div className="mt-3">
              <label className="block font-medium">الوصف</label>
              <Textarea
                className="w-full p-2 border rounded mt-1"
                placeholder="أدخل الوصف"
                value={editRecord.description}
                onChange={(e) =>
                  setEditRecord({ ...editRecord, description: e.target.value })
                }
              />
            </div>
            <div className="mt-3 text-end">
              <Button onClick={handleEdit} disabled={loadinEdit}>
                {loadinEdit ? "جاري الحفظ..." : "حفظ"}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </motion.div>
  );
}

const ShowAddSheet = ({ handleFetchCategories }) => {
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [addRecord, setAddRecord] = useState({
    title: "",
    description: "",
  });
  const [loadinEdit, setLoadinEdit] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    setLoadinEdit(true);

    try {
      await axios.post(`${BaseUrlApi}/categories`, {
        title: addRecord.title,
        description: addRecord.description,
      });
      toast("تمت الإضافة", { description: "تمت إضافة القسم الجديدة بنجاح 🎉" });

      await handleFetchCategories();
      setShowAddSheet(false);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadinEdit(false);
    }
  };

  return (
    <Sheet open={showAddSheet} onOpenChange={setShowAddSheet}>
      <SheetTrigger asChild>
        <Button>إضافة قسم</Button>
      </SheetTrigger>
      <SheetContent side="left" dir="rtl">
        <SheetHeader className="text-start">
          <SheetTitle>إضافة قسم جديد</SheetTitle>
        </SheetHeader>
        <div className="mt-3">
          <label className="block font-medium">العنوان</label>
          <Input
            className="w-full p-2 border rounded mt-1"
            placeholder="أدخل العنوان"
            value={addRecord.title || ""}
            onChange={(e) =>
              setAddRecord({ ...addRecord, title: e.target.value })
            }
          />
        </div>
        <div className="mt-3">
          <label className="block font-medium">الوصف</label>
          <Textarea
            className="w-full p-2 border rounded mt-1"
            placeholder="أدخل الوصف"
            value={addRecord.description || ""}
            onChange={(e) =>
              setAddRecord({ ...addRecord, description: e.target.value })
            }
          />
        </div>
        <div className="mt-3 text-end">
          <Button onClick={handleAdd} disabled={loadinEdit}>
            {loadinEdit ? "جاري الإضافة..." : "إضافة"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
